/* eslint-disable no-async-promise-executor */

import axios from 'axios';
import authStore from '../store/auth';
import { msalInstance } from '../App';
import { loginRequest } from './msal';
import toast from 'react-hot-toast';
import { getVersionHeaders, fetchVersionInfo } from './versioning';
import {
  auditStampRequest,
  auditHandleResponse,
  auditHandleError
} from '../services/audit/interceptors';
import { extractApiError } from '../utils';

export const httpRequest = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: 600000
});

export const agentApi = axios.create({
  baseURL: import.meta.env.VITE_AGENT_API_URL,
  timeout: 600000
});

export const refreshAzureToken = () =>
  new Promise(async (resolve, reject) => {
    try {
      const username = authStore.user.username;

      if (!msalInstance.getActiveAccount()) {
        // Replace 'accountIdentifier' with the actual account identifier

        const account = msalInstance.getAccountByUsername(username);

        msalInstance.setActiveAccount(account);
      }

      // Attempt to refresh the token
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest
      });

      resolve(tokenResponse);
    } catch (err) {
      reject(err);
    }
  });

const buildErrorData = (rawData, message, status, fallbackCode) => {
  if (
    rawData &&
    typeof rawData === 'object' &&
    !(rawData instanceof Blob) &&
    !Array.isArray(rawData)
  ) {
    return {
      detail: rawData.detail ?? rawData.message ?? message,
      message,
      error_code: rawData.error_code ?? fallbackCode,
      ...rawData
    };
  }

  if (Array.isArray(rawData)) {
    return {
      message,
      detail: rawData,
      details: rawData,
      error_code: fallbackCode
    };
  }

  if (typeof rawData === 'string' && rawData) {
    return {
      message: rawData,
      detail: rawData,
      error_code: fallbackCode
    };
  }

  return {
    message,
    detail: message,
    error_code: fallbackCode || (status ? `HTTP_${status}` : 'NETWORK_ERROR')
  };
};

const normalizeRejectedError = (error, rawData, message, status) => {
  const fallbackCode =
    error?.code === 'ECONNABORTED' ? 'TIMEOUT_ERROR' : status ? `HTTP_${status}` : 'NETWORK_ERROR';
  const normalizedData = buildErrorData(rawData, message, status, fallbackCode);

  error.data = normalizedData;
  if (error.response) {
    error.response.data = normalizedData;
  }

  return error;
};

const redirectToAccessDenied = () => {
  if (window.location.pathname !== '/access-denied') {
    window.location.replace('/access-denied');
  }
};

const getRequestPath = config => {
  const requestUrl = config?.url || '';

  try {
    return new URL(requestUrl, config?.baseURL || window.location.origin).pathname.replace(
      /^\/+/,
      ''
    );
  } catch {
    return requestUrl.split('?')[0].replace(/^\/+/, '');
  }
};

const shouldRedirectToAccessDenied = request => {
  if (request?.skipAccessDeniedRedirect) {
    return false;
  }

  return getRequestPath(request) === 'api/user-groups/user';
};

const errorHandler = async error => {
  const status = error?.response?.status;
  const originalRequest = error.config;
  let responseData = error?.response?.data;

  // When responseType is 'blob', error.response.data is a Blob — parse it to extract the detail
  let message = responseData?.detail;
  if (message === undefined && responseData instanceof Blob) {
    try {
      responseData = JSON.parse(await responseData.text());
      message = responseData?.message || responseData?.detail;
    } catch {
      // ignore — message stays undefined if blob parse fails
    }
  }

  // Also pick up new structured envelope { message } field
  if (!message) {
    message = responseData?.message;
  }

  const normalizedMessage =
    message || extractApiError(error, 'An error occurred. Please try again.');
  const normalizedError = normalizeRejectedError(error, responseData, normalizedMessage, status);

  if (status === 403 && shouldRedirectToAccessDenied(originalRequest)) {
    if (window.location.pathname !== '/access-denied') {
      toast.error(message || 'Access denied');
    }
    redirectToAccessDenied();

    return Promise.reject(normalizedError);
  }

  if (status === 401 && !originalRequest._retry) {
    originalRequest._retry = true; // Avoid infinite loops

    try {
      const tokenResponse = await refreshAzureToken();

      // Retry the failed request with the new token
      originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;

      // Acquire token silent success
      let accessToken = tokenResponse.accessToken;

      authStore.authorize(tokenResponse.account, accessToken);

      // The retried request will go back through responseHandler — audit fires there
      return httpRequest(originalRequest);
    } catch (refreshError) {
      // Handle token refresh error
      console.error('Token refresh error:', refreshError);
      auditHandleError(originalRequest, 401, 'Authentication failed');
      return Promise.reject(
        normalizeRejectedError(
          refreshError,
          refreshError?.response?.data,
          'Authentication failed',
          401
        )
      );
    }
  }

  auditHandleError(originalRequest, status ?? 0, normalizedMessage);

  return Promise.reject(normalizedError);
};

const agentErrorHandler = async error => {
  const status = error?.response?.status;
  const originalRequest = error.config;

  if (status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const tokenResponse = await refreshAzureToken();
      originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return agentApi(originalRequest);
    } catch (refreshError) {
      console.error('Agent API token refresh error:', refreshError);
      return Promise.reject(
        normalizeRejectedError(
          refreshError,
          refreshError?.response?.data,
          'Authentication failed',
          401
        )
      );
    }
  }

  return Promise.reject(
    normalizeRejectedError(
      error,
      error?.response?.data,
      extractApiError(error, 'An error occurred. Please try again.'),
      status
    )
  );
};

const requestHandler = async config => {
  await auditStampRequest(config);

  const token = authStore.token;
  const workflowId = config?.headers?.workflow_id || authStore.workflowId;

  // Get active company ID from localStorage (set by CompanySwitcher)
  const activeCompanyId = localStorage.getItem('activeCompanyId');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (workflowId) {
    config.headers.workflow_id = workflowId;
  }

  // Send active company ID as header for per-tab company context
  if (activeCompanyId) {
    config.headers['X-Active-Company-Id'] = activeCompanyId;
  }

  // Add version headers to all requests (don't overwrite per-request overrides)
  const versionHeaders = getVersionHeaders();
  Object.keys(versionHeaders).forEach(key => {
    if (config.headers[key] === undefined || config.headers[key] === null) {
      config.headers[key] = versionHeaders[key];
    }
  });

  return config;
};

const responseHandler = response => {
  // On first request, fetch version info from backend
  if (!responseHandler.versionFetched) {
    responseHandler.versionFetched = true;
    fetchVersionInfo()
      .then(versionInfo => {
        if (versionInfo) {
          console.log('Version info loaded from backend:', {
            schemaVersion: versionInfo.schema_version,
            supportedVersions: versionInfo.supported_schema_versions
          });
        }
      })
      .catch(err => {
        console.warn('Failed to fetch version info:', err);
      });
  }

  auditHandleResponse(response);

  return response.data;
};

httpRequest.interceptors.request.use(requestHandler);
httpRequest.interceptors.response.use(responseHandler, errorHandler);

agentApi.interceptors.request.use(requestHandler);
agentApi.interceptors.response.use(responseHandler, agentErrorHandler);
